import React, { PropsWithChildren, useEffect, useState } from 'react';
import { bindClick } from '../SiteUtil';
import { MetadataType, EventDateMetadata } from '../SiteClasses';
import { Dropdown } from 'semantic-ui-react';
import { Input, Checkbox, Table, Button, Header, Icon, Toast } from 'react-frontier';
import { firstBy } from 'thenby';
import API from '../API';
import classNames from 'classnames';
import AddMetadataModal from './AddMetadataModal';

interface EventFormProps{
	date_id: number,
	style?: React.CSSProperties,
}

interface QuestionInputProps{
	date_id: number,
	question: EventDateMetadata,
	readonly?: boolean,
	onUpdate: (metadata_id: number, value_id: number, value: string)=>void,
	onDelete: (metadata_id: number)=>void,
}

var QuestionInput = (props: QuestionInputProps)=>{
	var [sending, setSending] = useState<boolean>(false);
	var [editing, setEditing] = useState<boolean>(false);
	var [options, setOptions] = useState<{ value_id: number, value: string }[]>(null);
	var [value, setValue] = useState<string>(null);

	var revertUpdate = ()=>{
		if(props.question.type_id===MetadataType.CHECKBOX){
			setValue(null);
		}
	}

	useEffect(()=>{
		setValue(!editing ? null : (props.question.value_id ? props.question.value_id.toString() : (props.question.value || '')));
		setOptions(null);
		if(editing){
			if([MetadataType.DROPDOWN, MetadataType.MULTIPLE].indexOf(props.question.type_id)!==-1){
				setOptions(null);
				API.getMetadataValues(props.question.metadata_id).then(res=>{
					if(res.error){
						setEditing(false);
						revertUpdate();
						return Toast.error(res.message);
					}
					setOptions(res.data);
				}).catch(err=>{
					setEditing(false);
					revertUpdate();
					return Toast.error('Hubo un error inesperado cargando las opciones de la pregunta (LCL-EVMVGT-1)');
				})
			}
		}
	}, [editing]);

	var sendUpdate = (value_id: number, value: string)=>{
		if(sending || props.readonly) return;
		if(value_id){
			if(value_id===props.question.value_id){
				return setEditing(false);
			}
		}else{
			if((value || '').trim()===(props.question.value || '').trim()){
				return setEditing(false);
			}
		}
		setValue(value_id ? value_id.toString() : value);
		setSending(true);

		API.updateFormAnswer(props.date_id, props.question.metadata_id, value_id, value).then(res=>{
			if(res.error){
				revertUpdate();
				return Toast.error(res.message);
			}
			Toast.success('Se ha actualizado la respuesta del formulario');

			var value_text = value || value_id.toString();
			if(options && value_id){
				var v = options.find(a=>a.value_id===value_id);
				if(v) value_text = v.value;
			}

			props.onUpdate(props.question.metadata_id, value_id, value_text);
			setValue(null);
			setEditing(false);
		}).catch(err=>{
			revertUpdate();
			return Toast.error('Hubo un error inesperado actualizando el formulario (LCL-EVFRQI-1)');
		}).finally(()=>{
			setSending(false);
		});
	}
	
	if(props.question.type_id===MetadataType.CHECKBOX){
		return <div className="checkbox" style={{ minHeight: 60 }}>
			<div>
				<Checkbox checked={value ? value=='1' : props.question.value==='1'} label={props.question.metadata_comment || ''} onChange={props.readonly ? null : v=>sendUpdate(null, v ? '1' : '0')} style={{ paddingBottom: 5 }}/>
			</div>
			{sending && (
				<div className="fr loading inline"></div>
			)}
		</div>
	}else if(!editing){
		return <Input readonly textarea={props.question.type_id===MetadataType.TEXTAREA} textareaRows={2} value={props.question.value} placeholder={props.question.metadata_name || props.question.metadata_comment} style={{ margin: 0 }} button={(
			<Button iconName='pen' onClick={bindClick(setEditing, true)} style={{ display: 'flex', alignItems: 'center' }} 					className={classNames({
						box: props.question.type_id===MetadataType.TEXTAREA
					})}/>
		)} />
	}else if(props.question.type_id===MetadataType.DROPDOWN){
		return <div className="selection">
			<Dropdown selection
				placeholder={props.question.metadata_name || props.question.metadata_comment} 
				loading={!options}
				value={parseInt(value)}
				selectOnBlur={false}
				onChange={(e, d)=>{
					setValue(d.value.toString());
				}}
				options={!options ? [] : options.map(a=>({
					value: a.value_id,
					text: a.value
				}))}
			/>
			<Button iconName='check' color='green' loading={sending} onClick={()=>sendUpdate(parseInt(value), null)} />
		</div>
	}else{
		return <Input 
			textarea={props.question.type_id===MetadataType.TEXTAREA} 
			textareaRows={2} 
			style={{ margin: 0 }}
			placeholder={props.question.metadata_name || props.question.metadata_comment}
			value={value}
			onChange={setValue}
			button={(
				<Button
					iconName='check'
					onClick={()=>sendUpdate(null, value)}	
					className={classNames({
						box: props.question.type_id===MetadataType.TEXTAREA
					})}
				/>
			)}
		/>
	}
}

enum Modals{
	ADD = 1,
}

const EventForm = (props: EventFormProps)=>{
	var [form, setForm] = useState<EventDateMetadata[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [loading, setLoading] = useState<boolean>(false);

	useEffect(()=>{
		if(!props.date_id) return;
		API.getEventForm(props.date_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setForm(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error inesperado cargando el formulario (LCL-EVFRM-1)');
		});
	}, [props.date_id]);

	var onMetadataDeleted = (metadata_id: number)=>{
		setForm(form.filter(a=>a.metadata_id!==metadata_id));
	}

	var onMetadataUpdate = (metadata_id: number, value_id: number, value: string)=>{
		if(!form) return;
		var m = [...form];
		var mix = m.findIndex(a=>a.metadata_id===metadata_id);
		if(mix==-1) return;
		m[mix].value = value;
		m[mix].value_id = value_id;
		setForm(m);
	}

	var addMetadata = (metadata: EventDateMetadata)=>{
		var tx = Toast.show('Agregando pregunta al formulario', {
			loading: true,
			infinite: true,
			dismissable: false
		});
		API.updateFormAnswer(props.date_id, metadata.metadata_id, null, '').then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Se ha agregado la pregunta al formulario');
			setForm([
				...form,
				metadata,
			].sort(firstBy('metadata_id', 'asc')));
		}).catch(err=>{
			Toast.error('Hubo un error inesperado agregando la pregunta (LCL-EVFMADDM-1)');
		}).finally(()=>{
			Toast.dismiss(tx);
		})
		return true;
	}

	var refreshPreadded = ()=>{
		setLoading(true);
		API.refreshPreaddedMetadata(props.date_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			if(res.data.length>0){
				setForm([
					...form,
					...res.data,
				].sort(firstBy('metadata_id', 'asc')));
				Toast.success(`Se ha${res.data.length==1 ? '' : 'n'} agregado ${res.data.length} pregunta${res.data.length==1 ? '' : 's'}`);
			}else{
				Toast.success('No hay preguntas nuevas por agregar');
			}
		}).catch(err=>{
			Toast.error('Hubo un error inesperado refrescando las preguntas (LCL-EVFMRFSH-1)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	if(!props.date_id) return null;

	return <>
		<Table 
			striped 
			divided 
			details
			title='Formulario'
			className='ar form-list'
			style={{ maxWidth: 900, margin: 'auto', ...props.style }}
			button={(
				<Dropdown 
					icon={null}
					direction='left'
					trigger={(
						<Button iconName='pen' text='Opciones' size='tiny' />
					)}
				>
					<Dropdown.Menu>
						<Dropdown.Item text='Agregar pregunta' icon={'plus'} onClick={bindClick(setShownModal, Modals.ADD)} />
						<Dropdown.Item text='Refrescar' icon={'sync'} onClick={refreshPreadded} />
					</Dropdown.Menu>
				</Dropdown>
			)}
		>
			{(!form || loadError || loading) ? (
				<Table.Cell row colSpan={2}>
					<Header loading={!loadError} text={loadError || 'Cargando formulario'} style={{ paddingTop: 0 }} />
				</Table.Cell>
			) : form.length==0 ? (
				<Table.Cell row colSpan={2} empty>No hay preguntas en el formulario</Table.Cell>
			) : form.map(a=>(
				<Table.Row key={`MTIN-${a.metadata_id}`}>
					<Table.Cell className='question'>
						<div className="name">
							{a.metadata_name}
						</div>
						{!!a.metadata_comment && a.metadata_comment.length>0 && (
							<div className="comment">
								{a.metadata_comment}
							</div>
						)}
					</Table.Cell>
					<Table.Cell className={classNames('input', {
						checkbox: a.type_id===MetadataType.CHECKBOX
					})}>
						<QuestionInput date_id={props.date_id} question={a} onUpdate={onMetadataUpdate} onDelete={onMetadataDeleted} readonly={false} />
					</Table.Cell>
				</Table.Row>
			))}
		</Table>
		<AddMetadataModal
			open={shownModal===Modals.ADD}
			onClose={bindClick(setShownModal)}
			date_id={props.date_id}
			onAdd={addMetadata}
		/>
	</>
}

export default EventForm;